import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Lock } from "lucide-react";
import { AFFILIATE_URLS, onAffiliateClick } from "../config/affiliate";

/**
 * Provider Access CTA — homepage / partners-page block.
 * Compliance: research-grade language only. Affiliate disclosure inline.
 */
const ProviderAccessCTA = () => {
  return (
    <section className="py-16 bg-[#fffaf5]" data-testid="provider-access-cta">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#fdf2f4] via-[#faeae5] to-[#f7e0d2] border border-[#e8c5b8] p-8 md:p-14">
          {/* decorative accent */}
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-24 w-72 h-72 bg-[#d4a5ad]/20 rounded-full blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#b89a6d]/20 rounded-full blur-3xl"
          />

          <div className="relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/70 backdrop-blur border border-[#e8c5b8] rounded-full mb-5">
              <Lock className="w-3.5 h-3.5 text-[#b89a6d]" />
              <span className="text-xs uppercase tracking-wider text-[#b89a6d] font-semibold">
                Verified Provider Program
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#3a2a26] mb-5 leading-tight">
              Institutional pricing for
              <span className="block bg-gradient-to-r from-[#d4a5ad] to-[#b89a6d] bg-clip-text text-transparent">
                verified providers
              </span>
            </h2>

            <p className="text-base md:text-lg text-[#5a4a40] max-w-2xl leading-relaxed mb-8">
              Practices, clinics, and licensed researchers can apply for verified-provider access through
              <strong className="text-[#3a2a26]"> Alpha BioMed Labs</strong> for institutional-level
              account credentials and authorized pricing on research-grade materials. Multi-unit
              pricing tiers offer significant savings versus single-unit retail.
            </p>

            <div className="grid sm:grid-cols-3 gap-3 mb-8 text-sm">
              <div className="flex items-start gap-2.5 bg-white/60 backdrop-blur border border-[#f0d8d4] rounded-xl px-4 py-3" data-testid="provider-perk-0">
                <Sparkles className="w-4 h-4 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                <span className="text-[#5a4a40]"><strong className="text-[#3a2a26]">Volume tiers</strong> on multi-unit orders</span>
              </div>
              <div className="flex items-start gap-2.5 bg-white/60 backdrop-blur border border-[#f0d8d4] rounded-xl px-4 py-3" data-testid="provider-perk-1">
                <Sparkles className="w-4 h-4 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                <span className="text-[#5a4a40]"><strong className="text-[#3a2a26]">Authorized account</strong> credentials</span>
              </div>
              <div className="flex items-start gap-2.5 bg-white/60 backdrop-blur border border-[#f0d8d4] rounded-xl px-4 py-3" data-testid="provider-perk-2">
                <Sparkles className="w-4 h-4 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                <span className="text-[#5a4a40]"><strong className="text-[#3a2a26]">Documentation</strong> on request (COA, HPLC, MS)</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <a
                href={AFFILIATE_URLS.providerAccess}
                target="_blank"
                rel="noopener noreferrer nofollow sponsored"
                data-testid="provider-access-apply-btn"
                onClick={onAffiliateClick({ partner: "alphabiomedlabs", product: "provider-access", url: AFFILIATE_URLS.providerAccess })}
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#d4a5ad] to-[#c4949c] hover:from-[#c4949c] hover:to-[#b48390] text-white font-semibold rounded-full shadow-lg shadow-[#d4a5ad]/30 transition-all"
              >
                Apply for Provider Access
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <Link
                to="/provider-access"
                data-testid="provider-access-learn-more"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-[#b89a6d] hover:text-[#a08560] font-semibold text-sm"
              >
                Learn how it works →
              </Link>
            </div>

            <p className="mt-6 text-xs text-[#a09186] max-w-2xl">
              Affiliate disclosure: PeptidesForMedSpas may receive a referral credit when you create
              an Alpha BioMed Labs account through the link above. Pricing, eligibility, and account
              terms are determined by Alpha BioMed Labs. Research-grade materials are sold for
              laboratory research purposes only.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProviderAccessCTA;
